import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'

// Schema de validação
const registerSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome é obrigatório')
    .min(3, 'Nome deve ter no mínimo 3 caracteres'),
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
  phone: z
    .string()
    .min(1, 'Telefone é obrigatório')
    .transform(val => val.replace(/\D/g, ''))
    .pipe(z.string().regex(/^\d{10,11}$/, 'Telefone inválido (10 ou 11 dígitos com DDD)')),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(8, 'Senha deve ter no mínimo 8 caracteres')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Senha deve conter maiúscula, minúscula e número'),
  confirmPassword: z
    .string()
    .min(1, 'Confirmação de senha é obrigatória'),
  storeAddress: z.object({
    street: z.string().min(1, 'Rua é obrigatória'),
    number: z.string().min(1, 'Número é obrigatório'),
    complement: z.string().optional(),
    neighborhood: z.string().min(1, 'Bairro é obrigatório'),
    city: z.string().min(1, 'Cidade é obrigatória'),
    state: z.string().min(2, 'Estado é obrigatório').max(2, 'Use a sigla do estado'),
    zipcode: z.string().transform(val => val.replace(/\D/g, '')).pipe(z.string().length(8, 'CEP deve ter 8 dígitos')),
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas não conferem',
  path: ['confirmPassword'],
})

export default function Register() {
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState(1) // 1 = dados pessoais, 2 = endereço
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()

const {
    register,
    handleSubmit,
    trigger,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      storeAddress: {
        state: 'AM',
        city: 'Manaus',
      }
    }
  })

  // Avança para próximo passo
  const nextStep = async () => {
    const fieldsToValidate = ['name', 'email', 'phone', 'password', 'confirmPassword']
    const isValid = await trigger(fieldsToValidate)
    
    // Verifica manualmente se senhas coincidem
    const password = getValues('password')
    const confirmPassword = getValues('confirmPassword')
    
    if (password !== confirmPassword) {
      setError('confirmPassword', { message: 'Senhas não conferem' })
      return
    }
    
    if (isValid) {
      setStep(2)
    }
  }

  const onSubmit = async (data) => {
    // Remove confirmPassword antes de enviar
    const { confirmPassword, ...submitData } = data
    
    const result = await registerUser(submitData)
    if (result.success) {
      navigate('/dashboard')
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        Criar conta
      </h1>
      <p className="text-gray-600 mb-8">
        {step === 1 
          ? 'Preencha seus dados para começar.' 
          : 'Agora, informe o endereço da sua loja.'}
      </p>

      {/* Indicador de passos */}
      <div className="flex items-center gap-2 mb-8">
        <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-primary-600' : 'bg-gray-200'}`} />
        <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`} />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {step === 1 && (
          <>
            <Input
              label="Nome completo"
              placeholder="Seu nome"
              error={errors.name?.message}
              {...register('name')}
            />

            <Input
              label="Email"
              type="email"
              placeholder="seu@email.com"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Telefone (WhatsApp)"
              placeholder="(92) 99999-8888"
              hint="Com DDD. Pode digitar com ou sem formatação."
              error={errors.phone?.message}
              {...register('phone')}
            />

            <div className="relative">
              <Input
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                placeholder="Mínimo 8 caracteres"
                error={errors.password?.message}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <Input
              label="Confirmar senha"
              type={showPassword ? 'text' : 'password'}
              placeholder="Repita a senha"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <Button type="button" fullWidth onClick={nextStep}>
              Continuar
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Input
              label="CEP"
              placeholder="69000-000"
              error={errors.storeAddress?.zipcode?.message}
              {...register('storeAddress.zipcode')}
            />

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Input
                  label="Rua"
                  placeholder="Nome da rua"
                  error={errors.storeAddress?.street?.message}
                  {...register('storeAddress.street')}
                />
              </div>
              <Input
                label="Número"
                placeholder="123"
                error={errors.storeAddress?.number?.message}
                {...register('storeAddress.number')}
              />
            </div>

            <Input
              label="Complemento"
              placeholder="Apto, sala, etc. (opcional)"
              {...register('storeAddress.complement')}
            />

            <Input
              label="Bairro"
              placeholder="Nome do bairro"
              error={errors.storeAddress?.neighborhood?.message}
              {...register('storeAddress.neighborhood')}
            />

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Input
                  label="Cidade"
                  placeholder="Manaus"
                  error={errors.storeAddress?.city?.message}
                  {...register('storeAddress.city')}
                />
              </div>
              <Input
                label="UF"
                placeholder="AM"
                maxLength={2}
                error={errors.storeAddress?.state?.message}
                {...register('storeAddress.state')}
              />
            </div>

            <div className="flex gap-4">
              <Button 
                type="button" 
                variant="secondary" 
                fullWidth 
                onClick={() => setStep(1)}
              >
                Voltar
              </Button>
              <Button 
                type="submit" 
                fullWidth 
                isLoading={isSubmitting}
              >
                Criar conta
              </Button>
            </div>
          </>
        )}
      </form>

      <p className="mt-8 text-center text-gray-600">
        Já tem uma conta?{' '}
        <Link to="/login" className="text-primary-600 font-medium hover:underline">
          Fazer login
        </Link>
      </p>
    </div>
  )
}
